import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { compareAsc, getDay, addDays, startOfDay, format, parseISO } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import config from 'garaje/config/environment';
import type DeskLendingService from 'garaje/services/desk-lending';
import type { DeskLendingDateStatus } from 'garaje/services/desk-lending';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type MessageBusService from 'garaje/services/message-bus';
import type PerformanceLoadTime from 'garaje/services/performance-load-time';
import type RegistrationsService from 'garaje/services/registrations';
import type ScheduleGraphqlService from 'garaje/services/schedule-graphql';
import type StateService from 'garaje/services/state';
import { LENDING_STATUS } from 'garaje/utils/enums';
import { getReadableScheduleEligibilityOffset } from 'garaje/utils/getReadableScheduleEligibilityOffset';
import urlBuilder from 'garaje/utils/url-builder';

type ScheduleTab = 'plan-your-week' | 'attendance';

export default class ScheduleIndexController extends Controller {
  @service declare state: StateService;
  @service declare registrations: RegistrationsService;
  @service declare localStorage: LocalStorageService;
  @service declare featureConfig: FeatureConfigService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare abilities: AbilitiesService;
  @service declare performanceLoadTime: PerformanceLoadTime;
  @service declare deskLending: DeskLendingService;
  @service declare flashMessages: FlashMessagesService;
  @service declare messageBus: MessageBusService;
  @service declare scheduleGraphql: ScheduleGraphqlService;

  constructor(properties: object | undefined) {
    super(properties);
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  isTesting = config.environment === 'test';

  queryParams = ['selectedInviteId'];

  @tracked selectedInviteId: string | null = null;
  @tracked lendingStatusByDate: Record<string, DeskLendingDateStatus> = {};
  @tracked activeTab: ScheduleTab = 'plan-your-week';
  @tracked loginRequired = false;
  @tracked multiDateModalIsOpen = false;

  get viewAttendanceOnWebEnabled(): boolean {
    return this.featureFlags.isEnabled('view-attendance-on-web');
  }

  get attendanceIframeUrl(): string {
    const companyId = this.state.currentCompany.id;
    const locationId = this.state.currentLocation.id;
    const employeeId = (this.model as { employeeId?: string }).employeeId;
    return urlBuilder.attendance.calendarUrl(companyId, locationId, this.loginRequired, employeeId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get days() {
    return this.registrations.days.sort((a: { date: Date }, b: { date: Date }) => compareAsc(a.date, b.date));
  }

  get isPartialDayEnabled(): boolean {
    return this.abilities.can('use partial day booking desk');
  }

  get showRequiredDocumentExpiredBanner(): boolean {
    const noRequiredDocumentApproval = this.days.find((day) => !day.hasRequiredDocumentApproval);
    return !!noRequiredDocumentApproval;
  }

  get hasDesks(): boolean {
    return ((this.state.currentLocationEnabledDesks as { desksQuantity?: number })?.desksQuantity ?? 0) > 0;
  }

  get desksEnabled(): boolean {
    const { currentLocationEnabledDesks } = this.state;
    return (
      !!(this.state.features as { canAccessDesksApplication?: boolean })?.canAccessDesksApplication &&
      !!currentLocationEnabledDesks &&
      !!(currentLocationEnabledDesks as { active?: boolean }).active
    );
  }

  get bookDeskEnabled(): boolean {
    return (
      this.desksEnabled &&
      this.screeningFlowEnabled &&
      this.hasDesks &&
      this.featureConfig.isEnabled('deskReservations.booking')
    );
  }

  get getGroupDeskReservationsEnabled(): boolean {
    return this.locationFeatureFlags.isEnabled('group-desk-reservations');
  }

  get deskLendingEnabled(): boolean {
    return (
      this.abilities.can('lend desk for desks') &&
      !!(this.state.currentLocationEnabledDesks as { deskLendingEnabled?: boolean })?.deskLendingEnabled &&
      !!(this.model as { assignedDeskId?: string }).assignedDeskId
    );
  }

  get activeDays(): Array<{ dayOfWeek: string; active: boolean }> {
    const workplaceDays = (this.model as { workplaceDays?: Array<{ dayOfWeek: string; active: boolean }> })
      .workplaceDays;
    if (!workplaceDays) return [];
    return workplaceDays.filter((day) => day.active);
  }

  get screeningFlowEnabled(): boolean {
    return (this.state.currentLocation as { employeeScreeningEnabled?: boolean }).employeeScreeningEnabled ?? false;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get selectedDay() {
    // must check selectedInviteId because some screeningCards have an id of null in production
    if (!this.selectedInviteId) {
      return null;
    }

    // The day doesn't have an ID, so have to select on a related object's id
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    return this.days.find((day) => day.screeningCard?.id == this.selectedInviteId);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get indexedDays() {
    return this.days.map((day, index) => {
      return { ...day, index };
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  get weeks() {
    const days = this.indexedDays;
    if (days.length == 0) return [];
    let i = 0;
    const daysLeftAtStart = 7 - getDay(days[0]!.date);
    const weeks = [days.slice(0, daysLeftAtStart)];
    i += daysLeftAtStart;
    while (i < days.length) {
      weeks.push(days.slice(i, i + 7));
      i += 7;
    }
    return weeks;
  }

  get shouldLoadDays(): boolean {
    const { scheduleEligibilityOffset } = this.state.currentLocation as { scheduleEligibilityOffset?: number | null };
    if (scheduleEligibilityOffset == null) {
      return true;
    }
    return this.daysToLoad > 0;
  }

  get daysToLoad(): number {
    const { scheduleEligibilityOffset } = this.state.currentLocation as { scheduleEligibilityOffset?: number | null };

    if (scheduleEligibilityOffset == null) {
      return 30;
    }

    const offsetInDays = -scheduleEligibilityOffset / 1440 - this.days.length;
    return Math.min(offsetInDays, 30);
  }

  get readableScheduleEligibilityOffset(): string {
    return getReadableScheduleEligibilityOffset(
      (this.state.currentLocation as { scheduleEligibilityOffset?: number }).scheduleEligibilityOffset!,
    );
  }

  get isScheduleEligibilityOffsetNotificationVisible(): boolean {
    const { scheduleEligibilityOffset } = this.state.currentLocation as { scheduleEligibilityOffset?: number | null };
    const offset = this.storedScheduleEligibilityOffset;

    return offset !== scheduleEligibilityOffset && scheduleEligibilityOffset !== null;
  }

  get storedScheduleEligibilityOffset(): number | undefined {
    const { id } = this.state.currentLocation as { id: string };

    try {
      const offsets = this.localStorage.getItem('schedule_eligibility_offsets');
      const parsed = (offsets ? JSON.parse(offsets) : []) as { locationId: string; offset: number }[];
      const match = parsed.find((l) => l.locationId === id);

      return match?.offset;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }

    return undefined;
  }

  removeStoredScheduleEligibilityOffset(): void {
    const { id } = this.state.currentLocation as { id: string };

    try {
      const offsets = this.localStorage.getItem('schedule_eligibility_offsets');
      const parsed = (offsets ? JSON.parse(offsets) : []) as { locationId: string; offset: number }[];
      const index = parsed.findIndex((l) => l.locationId === id);
      if (index > -1) {
        parsed.splice(index, 1);
      }

      this.localStorage.setItem('schedule_eligibility_offsets', JSON.stringify(parsed));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  handleMessage = (message: { event: string; type?: string; msg?: string }): void => {
    if (message.event === 'showToaster' && message.type && message.msg) {
      this.flashMessages.showAndHideFlash(message.type as 'success' | 'info' | 'error' | 'warning', message.msg);
    }
  };

  @action
  closeScheduleEligibilityOffsetNotification(): void {
    const { id, scheduleEligibilityOffset } = this.state.currentLocation as {
      id: string;
      scheduleEligibilityOffset: number;
    };
    const locationItem = { locationId: id, offset: scheduleEligibilityOffset };

    try {
      const offsets = this.localStorage.getItem('schedule_eligibility_offsets');
      const parsed = (offsets ? JSON.parse(offsets) : []) as { locationId: string; offset: number }[];
      const index = parsed.findIndex((l) => l.locationId === id);
      if (index > -1) {
        parsed.splice(index, 1, locationItem);
      } else {
        parsed.push(locationItem);
      }

      this.localStorage.setItem('schedule_eligibility_offsets', JSON.stringify(parsed));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  @action
  logLoadTime(): void {
    this.performanceLoadTime.logPageUsableMetric();
  }

  @action
  setActiveTab(tab: ScheduleTab): void {
    this.activeTab = tab;
  }

  @action
  trackLoggedIn(): void {
    const waLoggedIn = this.localStorage.getItem('wa_logged_in');

    if (!waLoggedIn) {
      this.loginRequired = true;
      this.localStorage.setItem('wa_logged_in', true);
    } else {
      this.loginRequired = false;
    }
  }

  @action
  openMultiDateModal(): void {
    this.multiDateModalIsOpen = true;
  }

  @action
  closeMultiDateModal(): void {
    this.multiDateModalIsOpen = false;
  }

  @action
  async bulkSchedule(dates: string[]): Promise<void> {
    const deskId = (this.model as { assignedDeskId?: string }).assignedDeskId;

    const results = await Promise.allSettled(
      dates.map(async (dateStr) => {
        const date = parseISO(dateStr);
        const lendingStatus = this.lendingStatusByDate[dateStr]?.lendingStatus;

        if (lendingStatus === 'LENT_AVAILABLE' && deskId) {
          await this.scheduleGraphql.reclaimDeskReservation({ deskId, date });
          this.updateLendingStatusForDate(date, LENDING_STATUS.NOT_LENT);

          const updated = await this.scheduleGraphql.fetchRegistrationByDate({ date });
          if (updated) {
            await this.registrations.updateRegistration(date, () => ({
              ...updated,
              date,
              maxOccupancy: updated.maxOccupancy ?? undefined,
            }));
          }
        } else {
          await this.registrations.reserveDeskPartialDay(date);
        }
      }),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.filter((r) => r.status === 'rejected').length;

    if (failed === 0) {
      this.flashMessages.showAndHideFlash('success', `Scheduled ${succeeded} day${succeeded === 1 ? '' : 's'}`);
    } else {
      this.flashMessages.showAndHideFlash('error', `Scheduled ${succeeded} of ${dates.length} days. ${failed} failed.`);
    }
  }

  @action
  async bulkLend(dates: string[]): Promise<void> {
    const deskId = (this.model as { assignedDeskId?: string }).assignedDeskId as string;

    const alreadyLent = dates.filter((d) => this.lendingStatusByDate[d]?.lendingStatus === 'LENT_AVAILABLE');
    const datesToLend = dates.filter((d) => !alreadyLent.includes(d));

    if (datesToLend.length === 0) {
      this.flashMessages.showAndHideFlash(
        'success',
        `Desk already lent for ${alreadyLent.length} day${alreadyLent.length === 1 ? '' : 's'}`,
      );
      return;
    }

    await this.deskLending.lendDeskBulk(deskId, datesToLend);
    await this.fetchLendingStatus();

    const parts: string[] = [];
    parts.push(`Desk released for ${datesToLend.length} day${datesToLend.length === 1 ? '' : 's'}`);
    if (alreadyLent.length > 0) {
      parts.push(`${alreadyLent.length} already lent`);
    }
    this.flashMessages.showAndHideFlash('success', parts.join('. '));
  }

  @action
  async fetchLendingStatus(startDate?: Date, endDate?: Date): Promise<void> {
    if (!this.deskLendingEnabled) return;

    const deskId = (this.model as { assignedDeskId?: string }).assignedDeskId as string;
    const resolvedStart = startDate ?? startOfDay(new Date());
    const resolvedEnd =
      endDate ??
      (this.days.length > 0
        ? addDays(this.days[this.days.length - 1]!.date, 1)
        : addDays(resolvedStart, this.daysToLoad));

    try {
      const response = await this.deskLending.fetchLendingStatus(deskId, resolvedStart, resolvedEnd);
      const statusMap: Record<string, DeskLendingDateStatus> = {};
      for (const dateStatus of response.dates) {
        statusMap[dateStatus.date] = dateStatus;
      }
      this.lendingStatusByDate = { ...this.lendingStatusByDate, ...statusMap };
    } catch {
      // If lending status fetch fails, desk lending UI will show default (NOT_LENT) state
    }
  }

  @action
  async loadMoreAndFetchLending(daysToLoad: number): Promise<void> {
    const prevDayCount = this.days.length;
    await this.registrations.loadMoreTask.perform(daysToLoad);
    if (!this.deskLendingEnabled) return;
    const newDays = this.days.slice(prevDayCount);
    if (newDays.length === 0) return;
    const startDate = newDays[0]!.date;
    const endDate = addDays(newDays[newDays.length - 1]!.date, 1);
    await this.fetchLendingStatus(startDate, endDate);
  }

  @action
  updateLendingStatusForDate(date: Date, status: string): void {
    const dateKey = format(date, 'yyyy-MM-dd');
    this.lendingStatusByDate = {
      ...this.lendingStatusByDate,
      [dateKey]: { date: dateKey, lendingStatus: status } as DeskLendingDateStatus,
    };
  }
}
